import { useEffect, useRef } from "react";
import { motion } from "framer-motion";

/**
 * Section ambiences — each section gets a unique, lightweight atmosphere.
 * Pure CSS / canvas. No WebGL outside of the Hero.
 */

// ─────────────────────────────────────────────────────────────────────
// 1. Conic — slow rotating cone of violet light. Used in Services.
export function ConicAmbience() {
  return (
    <div
      aria-hidden="true"
      data-testid="ambience-conic"
      className="absolute inset-0 overflow-hidden pointer-events-none"
    >
      <div
        className="absolute -inset-[30%] opacity-60"
        style={{
          background:
            "conic-gradient(from 0deg at 50% 50%, transparent 0deg, rgba(183,148,246,0.08) 60deg, transparent 120deg, rgba(124,58,237,0.06) 220deg, transparent 300deg)",
          animation: "ambience-spin 60s linear infinite",
          filter: "blur(40px)",
        }}
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 50% 0%, rgba(183,148,246,0.05), transparent 60%)",
        }}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// 2. Vertical scan beam — used in Method.
export function ScanAmbience() {
  return (
    <div
      aria-hidden="true"
      data-testid="ambience-scan"
      className="absolute inset-0 overflow-hidden pointer-events-none"
    >
      <motion.div
        className="absolute top-0 bottom-0 w-[3px]"
        style={{
          background:
            "linear-gradient(to bottom, transparent 0%, rgba(183,148,246,0.5) 50%, transparent 100%)",
          filter: "blur(1.5px)",
          left: 0,
        }}
        animate={{ x: ["0vw", "100vw"] }}
        transition={{ duration: 22, repeat: Infinity, ease: "linear" }}
      />
      <motion.div
        className="absolute top-0 bottom-0 w-[2px]"
        style={{
          background:
            "linear-gradient(to bottom, transparent 0%, rgba(196,168,255,0.35) 50%, transparent 100%)",
          filter: "blur(2px)",
          left: 0,
        }}
        animate={{ x: ["-20vw", "100vw"] }}
        transition={{ duration: 28, repeat: Infinity, ease: "linear", delay: 6 }}
      />
      <div
        className="absolute inset-0 opacity-[0.4]"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(255,255,255,0.04) 1px, transparent 1px)",
          backgroundSize: "120px 100%",
          maskImage:
            "linear-gradient(to bottom, transparent, black 30%, black 70%, transparent)",
          WebkitMaskImage:
            "linear-gradient(to bottom, transparent, black 30%, black 70%, transparent)",
        }}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// 3. Filmstrip — horizontal scanlines + drifting violet spotlight. Projects.
export function FilmstripAmbience() {
  return (
    <div
      aria-hidden="true"
      data-testid="ambience-filmstrip"
      className="absolute inset-0 overflow-hidden pointer-events-none"
    >
      <div
        className="absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage:
            "repeating-linear-gradient(to bottom, rgba(255,255,255,0.04) 0px, rgba(255,255,255,0.04) 1px, transparent 1px, transparent 4px)",
          maskImage:
            "radial-gradient(ellipse at center, black 30%, transparent 90%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at center, black 30%, transparent 90%)",
        }}
      />
      <motion.div
        className="absolute rounded-full blur-[110px]"
        style={{
          width: 480,
          height: 480,
          top: "20%",
          left: "10%",
          background:
            "radial-gradient(circle, rgba(183,148,246,0.25), transparent 65%)",
        }}
        animate={{ x: ["0%", "70%", "20%", "0%"], y: ["0%", "30%", "60%", "0%"] }}
        transition={{ duration: 26, repeat: Infinity, ease: "easeInOut" }}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// 4. Mesh dots — grid of softly pulsing dots. WhyUs.
export function MeshDotsAmbience() {
  // Build a static dot mesh with CSS variables and animation delays
  const cols = 16;
  const rows = 9;
  const dots = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const delay = ((r + c) % 12) * 0.4 + (r % 3) * 0.7;
      dots.push(
        <span
          key={`${r}-${c}`}
          className="block w-1 h-1 rounded-full bg-[var(--accent)]/20"
          style={{
            animation: "ambience-dot-pulse 4.5s ease-in-out infinite",
            animationDelay: `${delay}s`,
          }}
        />
      );
    }
  }
  return (
    <div
      aria-hidden="true"
      data-testid="ambience-mesh-dots"
      className="absolute inset-0 overflow-hidden pointer-events-none"
    >
      <div
        className="absolute inset-0 grid place-items-center"
        style={{
          gridTemplateColumns: `repeat(${cols}, 1fr)`,
          gridTemplateRows: `repeat(${rows}, 1fr)`,
          maskImage:
            "radial-gradient(ellipse at center, black 35%, transparent 85%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at center, black 35%, transparent 85%)",
          padding: "8%",
        }}
      >
        {dots}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// 5. Code rain — falling monospace chars. Stack.
const RAIN_CHARS =
  "01<>{}[]/=+*#$@MLagenceREACTNEXTTSXAPI<div></div>{...}#fff";

export function CodeRainAmbience() {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf;
    let drops = [];
    let columns = 0;
    const fontSize = 14;

    const resize = () => {
      const parent = canvas.parentElement;
      const w = parent ? parent.clientWidth : window.innerWidth;
      const h = parent ? parent.clientHeight : 600;
      canvas.width = Math.floor(w * 0.65);
      canvas.height = Math.floor(h * 0.65);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      columns = Math.floor(canvas.width / fontSize);
      drops = Array.from({ length: columns }, () =>
        Math.random() * canvas.height
      );
    };
    resize();
    window.addEventListener("resize", resize);

    let last = 0;
    const draw = (t) => {
      if (t - last > 80) {
        last = t;
        ctx.fillStyle = "rgba(5, 5, 5, 0.10)";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.font = `${fontSize}px 'JetBrains Mono', monospace`;
        ctx.fillStyle = "rgba(183, 148, 246, 0.55)";
        for (let i = 0; i < drops.length; i++) {
          const c = RAIN_CHARS.charAt(
            Math.floor(Math.random() * RAIN_CHARS.length)
          );
          ctx.fillText(c, i * fontSize, drops[i]);
          if (drops[i] > canvas.height && Math.random() > 0.975) {
            drops[i] = 0;
          }
          drops[i] += fontSize;
        }
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <div
      aria-hidden="true"
      data-testid="ambience-code-rain"
      className="absolute inset-0 overflow-hidden pointer-events-none"
      style={{
        maskImage:
          "radial-gradient(ellipse at center, black 30%, transparent 80%)",
        WebkitMaskImage:
          "radial-gradient(ellipse at center, black 30%, transparent 80%)",
      }}
    >
      <canvas ref={ref} className="absolute inset-0 opacity-25" />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// 6. Side glow — pulsing violet halo on left side. FAQ.
export function SideGlowAmbience() {
  return (
    <div
      aria-hidden="true"
      data-testid="ambience-side-glow"
      className="absolute inset-0 overflow-hidden pointer-events-none"
    >
      <motion.div
        className="absolute rounded-full blur-[110px]"
        style={{
          width: 540,
          height: 540,
          top: "25%",
          left: "-12%",
          background:
            "radial-gradient(circle, rgba(183,148,246,0.22), transparent 65%)",
        }}
        animate={{ scale: [1, 1.18, 1], opacity: [0.55, 0.9, 0.55] }}
        transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute rounded-full blur-[120px]"
        style={{
          width: 420,
          height: 420,
          bottom: "10%",
          right: "-8%",
          background:
            "radial-gradient(circle, rgba(124,58,237,0.18), transparent 65%)",
        }}
        animate={{ scale: [1.05, 1, 1.05], opacity: [0.6, 0.4, 0.6] }}
        transition={{ duration: 11, repeat: Infinity, ease: "easeInOut", delay: 2 }}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// 7. Mesh blobs — multi-blob slow morphing. Contact.
export function MeshBlobsAmbience() {
  return (
    <div
      aria-hidden="true"
      data-testid="ambience-mesh-blobs"
      className="absolute inset-0 overflow-hidden pointer-events-none"
    >
      <motion.div
        className="absolute rounded-full blur-[100px]"
        style={{
          width: 480,
          height: 480,
          top: "10%",
          right: "10%",
          background:
            "radial-gradient(circle, rgba(183,148,246,0.30), transparent 65%)",
        }}
        animate={{ x: [0, -40, 30, 0], y: [0, 30, -20, 0], scale: [1, 1.1, 0.95, 1] }}
        transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute rounded-full blur-[120px]"
        style={{
          width: 400,
          height: 400,
          bottom: "5%",
          left: "10%",
          background:
            "radial-gradient(circle, rgba(124,58,237,0.22), transparent 65%)",
        }}
        animate={{ x: [0, 50, -20, 0], y: [0, -30, 20, 0], scale: [1.05, 0.95, 1.1, 1.05] }}
        transition={{ duration: 24, repeat: Infinity, ease: "easeInOut", delay: 3 }}
      />
      <motion.div
        className="absolute rounded-full blur-[140px]"
        style={{
          width: 520,
          height: 520,
          top: "40%",
          left: "40%",
          background:
            "radial-gradient(circle, rgba(196,168,255,0.18), transparent 65%)",
        }}
        animate={{ x: [0, -30, 40, 0], y: [0, 20, -30, 0] }}
        transition={{ duration: 28, repeat: Infinity, ease: "easeInOut", delay: 6 }}
      />
    </div>
  );
}
